// ChoiceTableHelpers.ts
import { AlignmentType, DateDisplayFormat, TableRowType } from "../Enums/UiEnums";
import * as DateUtils from "./DateUtils";
import * as StringFormat from "./StringFormat";
export interface IRowSelection {
    selected?:boolean
}

export type CellStyleDelegate = (col: ICol, row: Record<string, any>, rowType: TableRowType) => Record<string, string> | undefined;
export type CellClassDelegate = (col: ICol, row: Record<string, any>, rowType: TableRowType) => string[] | undefined;
export type BodyValueStrDelegate = (value: any, col: ICol, row: Record<string, any>) => string | undefined | null;
export type ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType) => string | undefined | null;
export type SortDelegate = (row: Record<string, any> | undefined) => object | undefined;

export enum RenderModeType{
    Selection = "Selection",            // A checkbox linked to the specified field and a check box in the header
    PrettyNumber = "PrettyNumber",      // Format a number with delimits and precision limits
    Percent = "Percent",                // Present a number as a percentage.  Also has precision limits.
    AccountingMoney="AccountingMoney",  // Add $, delimits, and () for negative numbers.  Also renders the number with red text by default.
    PrettyDate="PrettyDate",            // Format as a 'Pretty' date value
    DateTime="DateTime",                // Format as Date/Time, such as 05/30/2023 10:45AM
    HTML = "HTML",                      // Output HTML                
    BoolYesNo = "BoolYesNo",             // Render Yes/No for a boolean
    Clickable = "Clickable"             // Render as a clickable link
}

export interface ICol
{
    Name: string,                       // Used as the column header
    DataField?: string,                 // The associated data field
    ExtraData?: any,                    // Not used by the table proper - used to add things like hints used by scoped slot logic
    Alignment?: AlignmentType,          // Align the value (left -- default, right, center)
    RenderMode?: RenderModeType,        // Indicates how to handle the rendering of the DataField value
    StyleDel?: CellStyleDelegate,       // Function used to specify the cell style(s)
    ClassDel?: CellClassDelegate,       // Function used to specify the cell class(es)
    FormatBodyValue?: BodyValueStrDelegate, // Function used to generate text for a body cell
    FormatValue?: ValueStrDelegate,     // Function used to generate text for a cell
    InitialSortAscending?: boolean,     // The initial sort order (default is ascending)
    Sort?: string | SortDelegate | boolean,       // If a string, the field from the collection, if a SortDelegate a method that returns the value to sort by
    HideFilter?: boolean,               // Default: false
    FilterValue?: ValueStrDelegate,      // The text used to filter the column
    HeaderSelectionValue?: boolean       // If the column is marked with a RenderModeType.Selection. This would represent the value.
}

export type ITableColumns = ICol[]; // This makes it easier to spot table definitions

// Get the value for the cell as a formatted string
export function FormattedValueStr(col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | null| undefined {

    // Look for a render mode...
    switch(col.RenderMode)
    {
        case RenderModeType.Clickable:
            break;
        case RenderModeType.Selection:
            break;

        case RenderModeType.PrettyNumber:
            return StringFormat.ToPrettyNumber(GetCellValue(col, row));

        case RenderModeType.Percent:
            return StringFormat.ToPercentStr(GetCellValue(col, row));

        case RenderModeType.AccountingMoney:
            return StringFormat.ToAccountingMoneyStr(GetCellValue(col, row));

        case RenderModeType.PrettyDate:
            return DateUtils.prettyDate(GetCellValue(col, row));
        
        case RenderModeType.DateTime:
            const dateTime = GetCellValue(col, row);
            if (!dateTime)
                return;
            return `${DateUtils.toDisplayDateString(dateTime, DateDisplayFormat.NumericDate)} ${DateUtils.prettyTime(dateTime)}`;

        case RenderModeType.BoolYesNo:
            switch(GetCellValue(col, row))
            {
                case true: 
                return "Yes";

                case false: 
                return "No";
            }
            return;

        case undefined:
        case RenderModeType.HTML:
            break;

        default:
            alert("Un-handled render mode: " + col.RenderMode);
            break;
    }

    if (!col.DataField && !col.FormatValue && !col.FormatBodyValue)
        return;

    const cellValue = GetCellValue(col, row);

    // There is the potential both FormatBodyValue and FormatValue try to handle the value.
    //  For now we prioritize FormatBodyValue over FormatValue
    if (col.FormatBodyValue && TableRowType.Body === rowType)
    {
        return col.FormatBodyValue(cellValue, col, row ?? {});
    }        

    if (!col.FormatValue)
    {
        return cellValue;
    }

    return col.FormatValue(cellValue, col, row, rowType);
}

export function GetCellValue(col: ICol, row: Record<string, any> | undefined) : any
{
    return (row && col.DataField) ? row[col.DataField] : undefined;
}


// Make text for negative values red
const NegativeValuesRedTextStyle: CellStyleDelegate = (col: ICol, row: Record<string, any>, rowType: TableRowType): Record<string, string> | undefined => {
    if (TableRowType.Body == rowType || TableRowType.Footer == rowType)
    {
        const cellVal = GetCellValue(col, row);
        if ("number" === typeof cellVal)
        {
            if (cellVal < 0)  
                return {"color": "red"}; 
        }
    }
}

// A collection of stock style functions
export const CellStyles = {
    NegativeValuesRedTextStyle
}

// Get Rows with selected flag set to true, this is used in conjunction with the IRowSelection interface.
export function getSelectedTableItems(object_array: (unknown & IRowSelection)[], object_item?: string) : any[] {
    if (object_item) 
        return object_array.filter(o =>  o.selected ).map(o => (o as any)[object_item]);
    else
        return object_array.filter(o =>  o.selected ); 
} 
// Get Count of Rows with selected flag set to true, this is used in conjunction with the IRowSelection interface.
export function getSelectedTableCount(object_array: (unknown & IRowSelection)[]) : number {
    return object_array.filter(o =>  o.selected ).length ?? 0; 
} 

export function iColDateSort(input_date: DateUtils.NoTzDate |undefined, input_format: DateDisplayFormat) : Date | undefined {
    // Input format is mm/dd/yyyy output = yyyy-mm-dd
    if (!input_date) 
      return undefined;
    if ((input_date.length === 10) && ((input_format === DateDisplayFormat.NumericDate) || (input_format === DateDisplayFormat.NumericDashDate))) {
      return new Date(input_date + " 00:00:00");
    }
    if ((input_date.length === 12) && (input_format === DateDisplayFormat.PrettyDate)) {
      return new Date(input_date + " 00:00:00");
    }
    return undefined;
}

export interface IUniqueKeyId {
    uniqueKeyId: string
}

// Add a property named 'uniqueKeyId' with a unique value.  We need a unique key when sorting -- this provides one
//  Make sure to add this before adding the data to the table or it could change which can defeat the benefit of a unique key
export function addUniqueKeyId<TItem>(data: TItem[] | undefined): (TItem & IUniqueKeyId)[] | undefined
{
    if (!data)
        return;

    return (<(TItem & IUniqueKeyId)[]>data).map(item => {
        item.uniqueKeyId = crypto.randomUUID();
        return item;
    });
}

export function calculateGridRowCount(surroundingHeight: number, windowHeight?: number) {
    const rowHeight = 29;
    if (!windowHeight)
        windowHeight = window.innerHeight;
    let rowCount = ((windowHeight - surroundingHeight) / rowHeight) | 0;
    if (rowCount < 3) 
        rowCount = 3;
    return rowCount;    
}

